import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Sparkles, Shuffle } from "lucide-react";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { FactCard } from "../components/FactCard";
import { SubjectIcon } from "../components/SubjectIcon";
import { StreakReminder } from "../components/StreakReminder";
import { WeeklyWinnerBanner } from "../components/WeeklyWinnerBanner";
import { ChristmasFeature } from "../components/ChristmasFeature";

const Home = () => {
  const [subjects, setSubjects] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [latest, setLatest] = useState([]);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);
  const [results, setResults] = useState(null);
  const [surprising, setSurprising] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data));
    api.get("/facts/featured").then((r) => setFeatured(r.data));
    api.get("/facts/latest", { params: { limit: 6 } }).then((r) => setLatest(r.data));
  }, []);

  useEffect(() => {
    setTerm(q);
    if (q) {
      api.get(`/facts`, { params: { search: q } }).then((r) => setResults(r.data));
    } else {
      setResults(null);
    }
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    setParams(term.trim() ? { q: term.trim() } : {});
  };

  const surpriseMe = async () => {
    setSurprising(true);
    try {
      const res = await api.get("/facts/random");
      nav(`/curio/${res.data.slug}`);
    } catch (e) {
      setSurprising(false);
    }
  };

  return (
    <Layout>
      <StreakReminder />
      <ChristmasFeature />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#D8CEB9]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <p className="caption-label mb-5 cv-fade-up">Est. MMXXVI · A Cabinet of Curios</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1615] leading-[1.05] cv-fade-up" style={{ animationDelay: "80ms" }}>
            A Treasure Chest of<br />
            <span className="text-[#9E4535] italic">Curiosities</span>
          </h1>
          <p className="font-body text-base sm:text-lg text-[#4A4036] max-w-xl mx-auto mt-6 leading-relaxed cv-fade-up" style={{ animationDelay: "160ms" }}>
            Verified Curios from the natural world, forgotten history, and the far
            reaches of the universe — catalogued for the endlessly inquisitive.
          </p>

          <form onSubmit={submit} className="mt-9 max-w-lg mx-auto flex items-center relative cv-fade-up" style={{ animationDelay: "240ms" }}>
            <Search className="absolute left-4 w-5 h-5 text-[#7A6C5D]" />
            <input
              data-testid="hero-search-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search curios, tags, or subjects…"
              className="w-full font-ui text-base pl-12 pr-28 py-4 bg-white border border-[#D8CEB9] rounded-full outline-none focus:border-[#9E4535] focus:ring-4 focus:ring-[#F5E8EA] transition shadow-sm"
            />
            <button
              type="submit"
              data-testid="hero-search-button"
              className="absolute right-2 font-ui text-sm font-semibold px-5 py-2.5 bg-[#9E4535] text-[#FAF7F0] rounded-full hover:bg-[#82382B] transition-colors"
            >
              Search
            </button>
          </form>

          <div className="mt-5 cv-fade-up" style={{ animationDelay: "300ms" }}>
            <button
              type="button"
              onClick={surpriseMe}
              disabled={surprising}
              data-testid="surprise-me-button"
              className="group inline-flex items-center gap-2 font-ui text-sm font-semibold px-6 py-3 bg-transparent text-[#9E4535] border border-[#9E4535] rounded-full hover:bg-[#9E4535] hover:text-[#FAF7F0] transition-colors disabled:opacity-60"
            >
              <Shuffle className={`w-4 h-4 ${surprising ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
              {surprising ? "Opening a curiosity…" : "Surprise Me"}
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Top leaderboard */}

        {/* Search results */}
        {results !== null && (
          <section className="mb-16" data-testid="search-results">
            <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-[#1A1615] mb-2">
              {results.length} result{results.length !== 1 ? "s" : ""} for “{q}”
            </h2>
            <Link to="/" className="font-ui text-sm text-[#9E4535] hover:underline">Clear search</Link>
            {results.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                {results.map((f, i) => <FactCard key={f.slug} fact={f} index={i} />)}
              </div>
            ) : (
              <p className="font-body text-[#4A4036] mt-6">Nothing in the vault matches that yet. Try another term.</p>
            )}
          </section>
        )}

        {!results && (
          <>
            <WeeklyWinnerBanner />
            {/* Featured fact of the day */}
            {featured && (
              <section className="mb-20">
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <Sparkles className="w-5 h-5 text-[#C5A059]" />
                  <h2 className="caption-label !text-[#9E4535] !text-sm">Curio of the Day</h2>
                  {featured.cotd && (
                    <span className="flex flex-wrap items-center gap-2 font-mono-cv text-[10px] text-[#7A6C5D]" data-testid="cotd-badge">
                      <span className="px-2 py-0.5 border border-[#D8CEB9] rounded-full bg-[#FAF7F0]">Curio #{featured.cotd.position} of {featured.cotd.total}</span>
                      <span className="px-2 py-0.5 border border-[#D8CEB9] rounded-full bg-[#FAF7F0]">Cycle {featured.cotd.cycle}</span>
                      <span className="text-[#B8AC9C]">{featured.cotd.remaining} left until reset</span>
                    </span>
                  )}
                </div>
                <Link
                  to={`/curio/${featured.slug}`}
                  data-testid="featured-fact"
                  className="group grid md:grid-cols-2 bg-white border border-[#D8CEB9] rounded-sm overflow-hidden hover:border-[#9E4535] transition-colors"
                >
                  <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden bg-[#F3EDE0]">
                    <img src={featured.image_url || undefined} alt={featured.image_alt || featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="caption-label !text-[10px] bg-[#9E4535] text-[#FAF7F0] px-3 py-1 rounded-full">
                        {featured.subject?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-7 sm:p-10 flex flex-col justify-center">
                    <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1615] leading-tight group-hover:text-[#9E4535] transition-colors">
                      {featured.title}
                    </h3>
                    <p className="font-body text-base text-[#4A4036] mt-4 leading-relaxed">
                      {featured.hook}
                    </p>
                    <span className="inline-flex items-center gap-2 mt-6 font-ui text-sm font-semibold uppercase tracking-wider text-[#9E4535]">
                      Read More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </section>
            )}

            {/* Latest facts */}
            {latest.length > 0 && (
              <section className="mb-20" data-testid="latest-facts">
                <div className="flex items-end justify-between mb-8 border-b border-[#D8CEB9] pb-4">
                  <div>
                    <p className="caption-label mb-1">Fresh from the Vault</p>
                    <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-[#1A1615]">Latest Curios</h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {latest.map((f, i) => <FactCard key={f.slug} fact={f} index={i} />)}
                </div>
              </section>
            )}

            {/* Browse by subject */}
            <section id="subjects" className="scroll-mt-24">
              <div className="flex items-end justify-between mb-8 border-b border-[#D8CEB9] pb-4">
                <div>
                  <p className="caption-label mb-1">The Collection</p>
                  <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-[#1A1615]">Browse by Subject</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {subjects.map((s, i) => (
                  <Link
                    key={s.slug}
                    to={`/subject/${s.slug}`}
                    data-testid={`subject-card-${s.slug}`}
                    className="group relative bg-white border border-[#D8CEB9] rounded-sm p-6 hover:border-[#9E4535] hover:shadow-md transition-all duration-300 cv-fade-up"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <div className="absolute top-4 right-5 font-mono-cv text-xs text-[#B8AC9C]">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="w-12 h-12 rounded-full border-2 border-[#C5A059] flex items-center justify-center text-[#9E4535] group-hover:bg-[#9E4535] group-hover:text-[#FAF7F0] group-hover:border-[#9E4535] transition-colors mb-4">
                      <SubjectIcon name={s.icon} className="w-6 h-6" />
                    </div>
                    <h3 className="font-serif-display text-xl font-semibold text-[#1A1615] group-hover:text-[#9E4535] transition-colors">
                      {s.name}
                    </h3>
                    <p className="font-body text-sm text-[#4A4036] mt-2 leading-relaxed line-clamp-3">
                      {s.description}
                    </p>
                    <p className="caption-label mt-4 group-hover:text-[#9E4535] transition-colors">Enter Collection</p>
                  </Link>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Home;
